import { Outlet, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoMode } from '../../hooks/useDemoMode';
import { usePageMeta } from '../../hooks/usePageMeta';
import { saveLastPath } from '../access-control/SmartRedirect';
import { trackPageView } from '../../services/usage';
import { WhatsNewProvider, WhatsNewPanel, WhatsNewDialog, NoticeBar, entries } from '../../whats-new';
import type { Notice, WhatsNewEventName } from '../../whats-new';

/** The API root the What's New module talks to — this app's `/api/v1` (CLAUDE.md rule 6). */
const WHATS_NEW_API = '/api/v1';

/** Does a `page` of an entry or notice concern this route? The page itself and its sub-routes. */
function pageMatches(page: string, pathname: string): boolean {
    const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
    return path === page || path.startsWith(`${page}/`);
}

/**
 * The module's four moments, posted as `event` beside no ids — the API turns them into
 * `whats-new.<name>` feature rows. Fire and forget: telemetry may never be why a click fails.
 */
function postWhatsNewEvent(name: WhatsNewEventName): void {
    void fetch(`${WHATS_NEW_API}/whats-new/seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: name }),
    }).catch(() => { /* deliberately ignored */ });
}

export function Layout() {
    const { user } = useAuth();
    const location = useLocation();
    const [demoMode, setDemoMode] = useDemoMode();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const showSidebar = user?.isAdmin && !demoMode;

    // Sync tab title + per-page favicon with the active route.
    usePageMeta();

    // What's New (pilot 2026-10-07): the button is app-wide, the content is per page. An entry
    // or notice with a `page` shows on that route only; one without shows everywhere. Notices
    // for pages the person cannot open never arrive — the API filters those by CanView.
    const pageEntries = useMemo(
        () => entries.filter(e => !e.page || pageMatches(e.page, location.pathname)),
        [location.pathname],
    );
    const noticeFilter = useCallback(
        (n: Notice) => !n.page || pageMatches(n.page, location.pathname),
        [location.pathname],
    );

    // Persist current path so SmartRedirect can restore it after re-auth,
    // and record the page view (usage telemetry — no-op in dev/demo mode).
    useEffect(() => {
        saveLastPath(location.pathname);
        trackPageView(location.pathname);
    }, [location.pathname]);

    // Close the mobile nav drawer whenever the route changes.
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Ctrl+Shift+D toggles demo mode (admin-only) — hides the sidebar so admin
    // can show the app as a regular user would see it during live demos.
    useEffect(() => {
        if (!user?.isAdmin) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
                e.preventDefault();
                setDemoMode(!demoMode);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [user?.isAdmin, demoMode, setDemoMode]);

    return (
        <WhatsNewProvider apiBase={WHATS_NEW_API} entries={pageEntries} onEvent={postWhatsNewEvent} noticeFilter={noticeFilter}>
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {showSidebar && (
                <>
                    {/* Desktop: static sidebar (lg and up) */}
                    <div className="hidden lg:block">
                        <Sidebar />
                    </div>
                    {/* Below lg: hidden by default; opened as a slide-over drawer (also for admins) */}
                    {mobileNavOpen && (
                        <div className="fixed inset-0 z-50 lg:hidden">
                            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
                            <div className="relative h-full">
                                <Sidebar />
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className="flex-1 flex flex-col min-w-0">
                <Header onMenuClick={showSidebar ? () => setMobileNavOpen(true) : undefined} />
                <NoticeBar />
                <main className="flex-1 p-6 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
        <WhatsNewPanel />
        <WhatsNewDialog />
        </WhatsNewProvider>
    );
}
